import React, { useEffect, useRef, useState } from "react";
import { ClassificationLabels, DEPART_CHILDREN } from "../../models/classification.map";
import { visibleClassificationEntries } from "./patientFormRules";
import {
    DROP_OPTION_STYLE,
    HOVER_OPTION_BG,
    SELECTED_OPTION_BG,
    dropdownPanelStyle,
} from "./patientFormStyles";

const optionStyle = selected => ({
    ...DROP_OPTION_STYLE,
    width: "100%", border: 0, textAlign: "left",
    background: selected ? SELECTED_OPTION_BG : "",
    fontWeight: selected ? 600 : 400,
});

const resetOptionStyle = {
    ...DROP_OPTION_STYLE, width: "100%", textAlign: "left", background: "white", border: 0,
    color: "#94a3b8", borderBottom: "1px solid #f1f5f9",
};

const triggerStyle = hasValue => ({
    padding: "6px 10px", borderRadius: "7px", border: "1px solid #e2e8f0",
    fontSize: "13px", width: "100%", boxSizing: "border-box", background: "white",
    cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
    color: hasValue ? "#1e293b" : "#94a3b8",
});

const arrowStyle = { fontSize: "10px", color: "#94a3b8" };

function OptionButton({ label, selected, onClick, highlightSelectedOnHover = true }) {
    const restBg = selected ? SELECTED_OPTION_BG : "";
    const hoverBg = selected && highlightSelectedOnHover ? SELECTED_OPTION_BG : HOVER_OPTION_BG;
    return (
        <button
            type="button"
            onClick={onClick}
            style={optionStyle(selected)}
            onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
            onMouseLeave={e => { e.currentTarget.style.background = restBg; }}
        >{label}</button>
    );
}

function DepartSubmenu({ value, isOpen, isSelected, onOpen, onClose, onSelect }) {
    return (
        <div style={{ position: "relative" }} onMouseEnter={onOpen} onMouseLeave={onClose}>
            <button
                type="button"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label="Sous-classifications Départ"
                onClick={onOpen}
                style={{
                    ...optionStyle(isSelected),
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: isOpen || isSelected ? (isSelected ? SELECTED_OPTION_BG : HOVER_OPTION_BG) : "",
                }}
            >
                <span>Départ</span>
                <span style={arrowStyle}>◀</span>
            </button>
            {isOpen && (
                // Le retrait transparent évite de perdre le survol entre l'option et le volet.
                <div style={{ position: "absolute", right: "100%", top: 0, zIndex: 201, paddingRight: "4px" }}>
                    <div style={{
                        ...dropdownPanelStyle,
                        minWidth: "210px",
                        maxHeight: "280px",
                        overflowY: "auto",
                    }}>
                        {DEPART_CHILDREN.map(({ key, label }) => (
                            <OptionButton
                                key={key}
                                label={label}
                                selected={value === key}
                                onClick={e => { e.stopPropagation(); onSelect(key); }}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function useClickOutside(open, onClose) {
    const containerRef = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = e => { if (containerRef.current && !containerRef.current.contains(e.target)) onClose(); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open, onClose]);
    return containerRef;
}

function ClassificationOptions({ value, activeSubmenu, setActiveSubmenu, onSelect }) {
    const closeSubmenu = () => setActiveSubmenu(null);
    const current = value || "";
    return visibleClassificationEntries().map(([key, label]) => {
        if (key !== "URGENCE_CHRONIQUE") {
            return (
                <OptionButton key={key} label={label} selected={value === key}
                    onClick={() => onSelect(key)} />
            );
        }
        return (
            <React.Fragment key={key}>
                <OptionButton
                    label="Urgence chronique"
                    selected={value === key || current.startsWith("URGENCE_CHRONIQUE_")}
                    highlightSelectedOnHover={false}
                    onClick={() => onSelect(key)}
                />
                <DepartSubmenu
                    value={value}
                    isOpen={activeSubmenu === "DEPART"}
                    isSelected={current.startsWith("DEPART_")}
                    onOpen={() => setActiveSubmenu("DEPART")}
                    onClose={closeSubmenu}
                    onSelect={onSelect}
                />
            </React.Fragment>
        );
    });
}

export default function ClassificationSelect({ value, onChange, style }) {
    const [open, setOpen] = useState(false);
    const [activeSubmenu, setActiveSubmenu] = useState(null);
    const close = React.useCallback(() => { setOpen(false); setActiveSubmenu(null); }, []);
    const containerRef = useClickOutside(open, close);

    const displayLabel = value && value !== "UNKNOWN" ? (ClassificationLabels[value] || value) : null;
    const select = key => { onChange(key); close(); };

    return (
        <div ref={containerRef} style={{ position: "relative", ...style }}>
            <button
                type="button"
                data-testid="classification-trigger"
                onClick={() => { setOpen(o => !o); setActiveSubmenu(null); }}
                style={triggerStyle(displayLabel)}
            >
                <span>{displayLabel || "Sélectionner"}</span>
                <span style={{ ...arrowStyle, marginLeft: "6px" }}>▾</span>
            </button>

            {open && (
                <div style={{
                    ...dropdownPanelStyle,
                    position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 200,
                    overflow: "visible",
                }}>
                    <button
                        type="button"
                        onClick={() => select("")}
                        style={resetOptionStyle}
                        onMouseEnter={e => { e.currentTarget.style.background = HOVER_OPTION_BG; setActiveSubmenu(null); }}
                        onMouseLeave={e => { e.currentTarget.style.background = ""; }}
                    >Sélectionner</button>
                    <ClassificationOptions
                        value={value}
                        activeSubmenu={activeSubmenu}
                        setActiveSubmenu={setActiveSubmenu}
                        onSelect={select}
                    />
                </div>
            )}
        </div>
    );
}
